import { useMemo } from 'react';

/**
 * Hook to pre-populate form fields based on project context
 * Reduces manual data entry by auto-filling common fields
 */
export const useFormDefaults = (docCode, projectMeta, auditData, auditorOptions) => {
  return useMemo(() => {
    const defaults = {};
    const projectSites = [
      ...(Array.isArray(projectMeta?.sites) ? projectMeta.sites : []),
      ...(Array.isArray(projectMeta?.projectSites) ? projectMeta.projectSites : []),
    ];
    const additionalLocations = projectSites.map((site, index) => ({
      siteFunction: site?.siteName || site?.name || site?.siteFunction || `Site ${index + 1}`,
      scope: site?.scope || auditData?.scope || '',
      address: site?.siteAddress || site?.address || site?.location || '',
      source: 'multi-site',
    })).filter((site) => site.siteFunction || site.address);
    const standardsText = [auditData?.standards, ...(Array.isArray(projectMeta?.standards) ? projectMeta.standards : [])].join(' ');
    const requiresSoa = /\biso(?:\/iec)?\s*27\d{3}\b/i.test(standardsText.replace(/[-_:]+/g, ' '));

    // Common fields across all forms
    if (auditorOptions?.length > 0) {
      defaults.auditorIds = auditorOptions
        .filter(a => a.isLead)
        .map(a => a.id);
    }

    if (auditData?.leadAuditor) {
      defaults.leadAuditorId = auditData.leadAuditor.id;
      defaults.leadAuditorName = auditData.leadAuditor.name;
    }

    if (projectMeta) {
      defaults.clientName = projectMeta.clientName || '';
      defaults.locationName = projectMeta.locationName || '';
      defaults.country = projectMeta.country || '';
    }

    // Form-specific defaults
    switch (docCode) {
      case 'FAPP-01 Audit Plan':
        return {
          ...defaults,
          auditLanguage: 'Bahasa Indonesia',
          addressSameAsHeadOffice: 'yes',
          noOfShift: projectMeta?.shiftCount || '',
          applicableStandards: auditData?.standards || '',
          scopeOfCertification: auditData?.scope || '',
          preparedBy: auditData?.leadAuditor?.name || '',
          location: projectMeta?.locationName || '',
          rundowns: [{
            type: 'Event',
            timeFrom: '',
            timeTo: '',
            locationSite: projectMeta?.locationName || 'On-site',
            auditorIds: defaults.auditorIds || [],
            clientProcesses: '',
            processOwner: '',
            clausulAnnex: '',
          }],
        };

      case 'FAPP-05 Audit Report':
        return {
          ...defaults,
          applicableStandards: auditData?.standards || '',
          scopeOfCertification: auditData?.scope || '',
          location: projectMeta?.locationName || '',
          leadAuditorRecommendation: 'rec1',
          findingsSummary: {
            majorIssued: 0,
            majorClosed: 0,
            minorIssued: 0,
            minorClosed: 0,
            openNCs: 0,
          },
        };

      case 'FAPP-06 Certificate Information Form':
      case 'FAPP-06 CIF (signed by client)':
        return {
          ...defaults,
          clientName: projectMeta?.clientName || '',
          contact: projectMeta?.contactPerson || '',
          position: projectMeta?.contactPosition || '',
          phone: projectMeta?.contactPhone || '',
          email: projectMeta?.contactEmail || '',
          direction: projectMeta?.direction || '',
          address: projectMeta?.address || '',
          additionalLocations,
          requiresSoa,
          statementOfApplicability: '',
          soa: '',
        };

      case 'FTRD-01 Technical Review and Certification Decision':
        return {
          ...defaults,
          applicableStandards: auditData?.standards || '',
          scopeOfCertification: auditData?.scope || '',
          reviewDate: new Date().toISOString().slice(0, 10),
          part1: {
            packageComplete: { value: '', details: '' },
            technicalReviewWaived: { value: '', details: '' },
            auditMandaysExecuted: { value: '', details: '' },
            auditTeamAssigned: { value: '', details: '' },
            gradingOfFindings: { value: '', details: '' },
            writingOfFindings: { value: '', details: '' },
            verificationOfNC: { value: '', details: '' },
            completenessRecertification: { value: '', details: '' },
            auditRecords: { value: '', details: '' },
            validityOfCertification: { value: '', details: '' },
            auditMandatoryElements: { value: '', details: '' },
            auditPlanning: { value: '', details: '' },
            exclusions: { value: '', details: '' },
          },
          part2: {
            capMinorAccepted: { value: '', details: '' },
            caMajorAccepted: { value: '', details: '' },
            approvalLeadAuditor: { value: '', details: '' },
          },
          part3: {
            acceptabilityOfResults: { value: '', details: '' },
            recertificationAudits: { value: '', details: '' },
            currentCertValid: { value: '', details: '' },
            leadAuditorApproved: { value: '', details: '' },
          },
        };

      case 'FAPP-08 Change Notice Form':
        return {
          ...defaults,
          clientCompany: projectMeta?.clientName || '',
          noticeDate: new Date().toISOString().slice(0, 10),
        };

      case 'FTRD-02 Certificate Review and Production':
        return {
          ...defaults,
          clientName: projectMeta?.clientName || '',
          addressClient: projectMeta?.address || '',
          naceCode: projectMeta?.eaCode || '',
          certificateScope: auditData?.scope || '',
          applicableStandards: auditData?.standards || '',
          certificationDecision: 'GRANT',
          signatureTitle: 'Certification Manager',
        };

      case 'Audit Summary Report':
        return {
          ...defaults,
          auditLeadId: auditData?.leadAuditor?.id || '',
          leadAuditorName: auditData?.leadAuditor?.name || '',
          applicableStandards: auditData?.standards || '',
          majorNC: 0,
          minorNC: 0,
          ofi: 0,
          observations: 0,
          conformities: 0,
          conclusion: 'Recommend Certification',
        };

      default:
        return defaults;
    }
  }, [docCode, projectMeta, auditData, auditorOptions]);
};
